/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Divider, Typography, Typography2 } from '@coursera/cds-core';
import { StarFilledIcon, ThumbsUpFilledIcon } from '@coursera/cds-icons';

import { ENROLLMENT_COUNT_THRESHOLD } from 'bundles/unified-description-page-common/constants';

import _t from 'i18n!nls/xdp';

export type ReviewAndSatisfactionScoreProps = {
  productRating?: number | null;
  totalReviewCount?: number | null;
  enrollmentCount?: number | null;
  satisfactionScore?: number | null;
  satisfactionScoreSubtitle?: string | null;
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
    align-content: center;
  `,
  iconContainer: css`
    padding: 0 var(--cds-spacing-100) 0 var(--cds-spacing-50);
    display: flex;
    align-items: center;
  `,
  satisfactionWrapper: css`
    display: flex;
    align-items: center;
  `,
  scoreWrapper: css`
    margin-left: var(--cds-spacing-100);
  `,
  divider: css`
    height: var(--cds-spacing-300);
    margin-right: var(--cds-spacing-100);
  `,
};

const KeyInformationReviewsAndSatisfactionScoreItem = ({
  productRating,
  totalReviewCount,
  enrollmentCount,
  satisfactionScore,
  satisfactionScoreSubtitle,
}: ReviewAndSatisfactionScoreProps) => {
  if (!productRating || !totalReviewCount || !satisfactionScore) return null;

  const roundedSatisfactionScore = Math.round(satisfactionScore);
  const shouldDisplayEnrollmentCount = enrollmentCount && enrollmentCount > ENROLLMENT_COUNT_THRESHOLD;
  const fixedRating = productRating.toFixed(1);

  return (
    <React.Fragment>
      <div css={styles.wrapper}>
        <Typography aria-label={_t('#{fixedRating} stars', { fixedRating })} variant="h2semibold" component="div">
          {fixedRating}
        </Typography>
        <div css={styles.iconContainer}>
          <StarFilledIcon color="interactive" size="medium" />
        </div>

        <div css={styles.satisfactionWrapper}>
          <Divider css={styles.divider} color="light" orientation="vertical" />
          <ThumbsUpFilledIcon color="interactive" />
          <Typography2
            aria-label={
              satisfactionScoreSubtitle ||
              // eslint-disable-next-line no-restricted-syntax
              _t('#{roundedSatisfactionScore}%. Most learners liked this course.', {
                roundedSatisfactionScore,
              })
            }
            css={styles.scoreWrapper}
            component="span"
            variant="subtitleLarge"
          >
            {`${roundedSatisfactionScore}%`}
          </Typography2>
        </div>
      </div>
      <Typography2 component="p" variant="bodySecondary" color="supportText">
        {_t('(#{totalReviewCount} reviews)', {
          totalReviewCount: totalReviewCount.toLocaleString(),
        })}
      </Typography2>

      {shouldDisplayEnrollmentCount && (
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          <FormattedMessage message={_t('{count} enrolled')} count={<FormattedNumber value={enrollmentCount} />} />
        </Typography2>
      )}
    </React.Fragment>
  );
};

export default KeyInformationReviewsAndSatisfactionScoreItem;
